import { FC } from "react";
import { Folder } from "~/api/model";
import { useGetProjectDocumentsStatuses } from "~/api/projects/projects";
import { CaseStatusBadge } from "~/components/CaseStatusBadge";
import { Skeleton } from "~/components/ui/skeleton";
import { UserAvatar } from "~/components/ui/user-avatar";
import { formatDate, useOrganizationParams } from "~/helpers";
import { cn } from "~/lib/utils";
import { formatDateOnly } from "~/utils/date";
import { CaseActions } from "./CaseActions";

type CaseListItemProps = {
  caseItem: Folder;
  onCaseClick: (caseId: string) => void;
  columnWidths: Record<string, string>;
  style?: React.CSSProperties;
};

export const CaseListItem: FC<CaseListItemProps> = ({
  caseItem,
  onCaseClick,
  columnWidths,
  style,
}) => {
  const { organizationId } = useOrganizationParams();
  const { data: counts, isLoading: countsLoading } =
    useGetProjectDocumentsStatuses(organizationId, caseItem.id);

  return (
    <div
      style={style}
      className="flex items-center hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
      onClick={() => onCaseClick(caseItem.id)}
    >
      <div
        className={cn(
          "px-4 py-4 font-medium text-zinc-500 text-sm truncate",
          columnWidths.name
        )}
      >
        {caseItem.name}
      </div>

      <div className={cn("px-4 text-zinc-500 text-sm", columnWidths.status)}>
        {countsLoading || !counts ? (
          <Skeleton className="h-[24px] w-[24px] rounded-full" />
        ) : (
          <CaseStatusBadge counts={counts} chatStatus={caseItem.chatStatus} />
        )}
      </div>

      <div
        className={cn("px-4 text-zinc-500 text-sm", columnWidths.dateOfBirth)}
      >
        {formatDateOnly(caseItem.dateOfBirth)}
      </div>

      <div
        className={cn("px-4 text-zinc-500 text-sm", columnWidths.dateOfLoss)}
      >
        {formatDateOnly(caseItem.dateOfLoss)}
      </div>

      <div
        className={cn(
          "px-4 text-zinc-500 text-sm truncate",
          columnWidths.claimNumber
        )}
      >
        {caseItem.claimNumber}
      </div>

      <div className={cn("px-4 text-zinc-500 text-sm", columnWidths.created)}>
        {formatDate(caseItem.createdAt)}
      </div>

      <div className={cn("px-4 text-zinc-500 text-sm", columnWidths.modified)}>
        {formatDate(caseItem.updatedAt)}
      </div>

      <div className={cn("px-4 text-zinc-500 text-sm", columnWidths.createdBy)}>
        {caseItem?.author?.name ? (
          <div className="flex items-center gap-2">
            <UserAvatar name={caseItem.author.name} size="sm" />
            <span className="text-zinc-500 text-sm font-normal">
              {caseItem.author.name}
            </span>
          </div>
        ) : (
          "-"
        )}
      </div>

      <div
        className={cn("px-4 text-right", columnWidths.actions)}
        onClick={(e) => e.stopPropagation()}
      >
        <CaseActions caseId={caseItem.id} />
      </div>
    </div>
  );
};
